import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import './CampaignAnalytics.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CampaignAnalytics = () => {
  const navigate = useNavigate();
  const { user, sessionToken } = useAuth();

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [stats, setStats] = useState({
    total_campaigns: 0,
    total_emails_sent: 0,
    total_opens: 0,
    total_clicks: 0,
    avg_open_rate: 0,
    avg_click_rate: 0
  });

  useEffect(() => {
    if (user && user.is_merchant) {
      loadCampaigns();
    }
  }, [user]);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/merchant/campaigns`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });

      const campaignData = response.data.campaigns || [];
      setCampaigns(campaignData);

      // Calculate overall stats
      const totalSent = campaignData.reduce((sum, c) => sum + (c.emails_sent || 0), 0);
      const totalOpens = campaignData.reduce((sum, c) => sum + (c.emails_opened || 0), 0);
      const totalClicks = campaignData.reduce((sum, c) => sum + (c.emails_clicked || 0), 0);

      setStats({
        total_campaigns: campaignData.length,
        total_emails_sent: totalSent,
        total_opens: totalOpens,
        total_clicks: totalClicks,
        avg_open_rate: totalSent > 0 ? ((totalOpens / totalSent) * 100).toFixed(1) : 0,
        avg_click_rate: totalSent > 0 ? ((totalClicks / totalSent) * 100).toFixed(1) : 0
      });

    } catch (err) {
      console.error('Error loading campaigns:', err);
      setError('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Draft', color: '#999' },
      ready: { label: 'Ready', color: '#ffa500' },
      sent: { label: 'Sent', color: '#4CAF50' },
      failed: { label: 'Failed', color: '#f44336' }
    };

    const config = statusConfig[status] || statusConfig.draft;

    return (
      <span className="status-badge" style={{ backgroundColor: config.color }}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const calculateEngagement = (campaign) => {
    const sent = campaign.emails_sent || 0;
    const opens = campaign.emails_opened || 0;
    const clicks = campaign.emails_clicked || 0;

    return {
      openRate: sent > 0 ? ((opens / sent) * 100).toFixed(1) : 0,
      clickRate: sent > 0 ? ((clicks / sent) * 100).toFixed(1) : 0,
      clickThroughRate: opens > 0 ? ((clicks / opens) * 100).toFixed(1) : 0
    };
  };

  if (!user || !user.is_merchant) {
    return (
      <Layout>
        <div className="access-denied">
          <h2>⚠️ Merchant Access Required</h2>
          <p>This page is only accessible to merchants.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="campaign-analytics-page">
        <div className="analytics-header">
          <h1>📊 Campaign Analytics</h1>
          <button
            className="create-campaign-btn"
            onClick={() => navigate('/merchant/campaigns/create')}
          >
            ➕ Create New Campaign
          </button>
        </div>

        {/* Overall Stats */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📧</div>
            <div className="stat-content">
              <h3>{stats.total_campaigns}</h3>
              <p>Total Campaigns</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">📤</div>
            <div className="stat-content">
              <h3>{stats.total_emails_sent}</h3>
              <p>Emails Sent</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">👁️</div>
            <div className="stat-content">
              <h3>{stats.total_opens}</h3>
              <p>Total Opens</p>
              <span className="stat-rate">{stats.avg_open_rate}% open rate</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">🖱️</div>
            <div className="stat-content">
              <h3>{stats.total_clicks}</h3>
              <p>Total Clicks</p>
              <span className="stat-rate">{stats.avg_click_rate}% click rate</span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading campaigns...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <p>⚠️ {error}</p>
            <button onClick={loadCampaigns}>Retry</button>
          </div>
        ) : campaigns.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <h2>No Campaigns Yet</h2>
            <p>Create your first marketing campaign to reach your consumers!</p>
            <button
              className="create-first-btn"
              onClick={() => navigate('/merchant/campaigns/create')}
            >
              Create Your First Campaign
            </button>
          </div>
        ) : (
          <>
            {/* Campaigns List */}
            <div className="campaigns-section">
              <h2>Your Campaigns</h2>
              <div className="campaigns-table">
                <div className="table-header">
                  <div>Campaign Name</div>
                  <div>Status</div>
                  <div>Consumers</div>
                  <div>Sent</div>
                  <div>Opens</div>
                  <div>Clicks</div>
                  <div>Date</div>
                  <div>Actions</div>
                </div>

                {campaigns.map(campaign => {
                  const engagement = calculateEngagement(campaign);
                  
                  return (
                    <div key={campaign.id} className="table-row">
                      <div className="campaign-name">
                        <strong>{campaign.campaign_name}</strong>
                      </div>
                      <div>{getStatusBadge(campaign.status)}</div>
                      <div>{campaign.total_consumers || 0}</div>
                      <div>{campaign.emails_sent || 0}</div>
                      <div>
                        {campaign.emails_opened || 0}
                        <span className="engagement-rate">({engagement.openRate}%)</span>
                      </div>
                      <div>
                        {campaign.emails_clicked || 0}
                        <span className="engagement-rate">({engagement.clickRate}%)</span>
                      </div>
                      <div className="date-cell">
                        {formatDate(campaign.sent_at || campaign.created_at)}
                      </div>
                      <div>
                        <button
                          className="view-btn"
                          onClick={() => setSelectedCampaign(campaign)}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Campaign Detail Modal */}
        {selectedCampaign && (
          <div className="modal-overlay" onClick={() => setSelectedCampaign(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{selectedCampaign.campaign_name}</h2>
                <button
                  className="close-modal-btn"
                  onClick={() => setSelectedCampaign(null)}
                >
                  ×
                </button>
              </div>

              <div className="modal-body">
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Status:</span>
                    <span>{getStatusBadge(selectedCampaign.status)}</span>
                  </div>

                  <div className="detail-item">
                    <span className="detail-label">Created:</span>
                    <span>{formatDate(selectedCampaign.created_at)}</span>
                  </div>

                  <div className="detail-item">
                    <span className="detail-label">Sent:</span>
                    <span>{formatDate(selectedCampaign.sent_at)}</span>
                  </div>

                  <div className="detail-item">
                    <span className="detail-label">Consumers:</span>
                    <span>{selectedCampaign.total_consumers}</span>
                  </div>

                  <div className="detail-item">
                    <span className="detail-label">Products:</span>
                    <span>{selectedCampaign.total_products}</span>
                  </div>

                  <div className="detail-item">
                    <span className="detail-label">Try-ons Generated:</span>
                    <span>{selectedCampaign.generated_tryons?.length || 0}</span>
                  </div>
                </div>

                <div className="engagement-section">
                  <h3>Engagement Metrics</h3>
                  <div className="engagement-bars">
                    <div className="engagement-bar">
                      <div className="bar-label">
                        <span>Emails Sent</span>
                        <span>{selectedCampaign.emails_sent || 0}</span>
                      </div>
                      <div className="bar-track">
                        <div className="bar-fill" style={{ width: '100%' }}></div>
                      </div>
                    </div>

                    <div className="engagement-bar">
                      <div className="bar-label">
                        <span>Opened</span>
                        <span>{selectedCampaign.emails_opened || 0} ({calculateEngagement(selectedCampaign).openRate}%)</span>
                      </div>
                      <div className="bar-track">
                        <div
                          className="bar-fill bar-open"
                          style={{ width: `${calculateEngagement(selectedCampaign).openRate}%` }}
                        ></div>
                      </div>
                    </div>

                    <div className="engagement-bar">
                      <div className="bar-label">
                        <span>Clicked</span>
                        <span>{selectedCampaign.emails_clicked || 0} ({calculateEngagement(selectedCampaign).clickRate}%)</span>
                      </div>
                      <div className="bar-track">
                        <div
                          className="bar-fill bar-click"
                          style={{ width: `${calculateEngagement(selectedCampaign).clickRate}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>

                {selectedCampaign.generated_tryons && selectedCampaign.generated_tryons.length > 0 && (
                  <div className="tryons-section">
                    <h3>Generated Try-Ons ({selectedCampaign.generated_tryons.length})</h3>
                    <div className="tryons-preview-grid">
                      {selectedCampaign.generated_tryons.slice(0, 6).map((tryon, idx) => (
                        <div key={idx} className="tryon-preview-item">
                          <img src={tryon.result_image} alt={tryon.product_name} />
                          <div className="tryon-info">
                            <p className="consumer-name">{tryon.consumer_name}</p>
                            <p className="product-name">{tryon.product_name}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    {selectedCampaign.generated_tryons.length > 6 && (
                      <p className="more-tryons">+ {selectedCampaign.generated_tryons.length - 6} more</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CampaignAnalytics;
